/**
 * Backorder display logic (port of Cornerstone 6.19-6.21, no pick-list support).
 *
 * Pure functions only: no DOM, no jQuery, no window. product-details.js owns
 * the DOM writes; this module owns the arithmetic and message resolution so it
 * can be unit-tested in Vitest's node environment.
 *
 * Conventions (spec docs/superpowers/specs/2026-09-03-backorder-support-design.md):
 *  - availableToSell 0 / missing = unknown => never a cap.
 *  - unlimited === true => infinite backorder and no cap.
 *  - messageId is ALWAYS overwritten by mergeBackorderData (stale-variant fix).
 *  - The DEFAULT message gives way to a live ship-by date when the onlineservices
 *    GetInventory result carries a usable nextAvailableDate (today or later);
 *    assigned messages are never touched.
 */

const DEFAULT_QTY_MESSAGE = '__QTY__ will be backordered';
const DEFAULT_MAX_MESSAGE = 'The maximum purchasable quantity is __QTY__';
const DEFAULT_SHIP_BY_MESSAGE = 'Backordered items are expected to ship by __DATE__';
const DAY_MS = 24 * 60 * 60 * 1000;

function asNumber(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function toInt(value, fallback = 0) {
    const n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
}

export function buildBackorderContext(context = {}) {
    const onHand = asNumber(context.backorderAvailableOnHand) !== null
        ? context.backorderAvailableOnHand
        : toInt(context.backorderAvailableOnHand, toInt(context.backorderStockLevel, 0));

    return {
        onHand,
        availableForBackorder: toInt(context.backorderAvailableForBackorder, 0),
        availableToSell: toInt(context.backorderAvailableToSell, 0),
        unlimited: context.backorderUnlimited === true,
        // Cornerstone: an undefined "qty on backorder" flag means show.
        showQtyOnBackorder: context.backorderShowQuantity !== false,
        showMessage: context.backorderShowMessage === true,
        showPrompt: context.backorderShowPrompt === true,
        // True only when the store EXPLICITLY enables a backorder display. available_for_backorder
        // and backorder_message_id exist only in the option-change response, so products without
        // options need one init fetch - but only on stores that actually show backorders.
        hasDisplayFlag: context.backorderShowMessage === true
            || context.backorderShowPrompt === true
            || context.backorderShowQuantity === true,
        promptText: typeof context.backorderPrompt === 'string' ? context.backorderPrompt : '',
        messages: Array.isArray(context.backorderMessages) ? context.backorderMessages : [],
        messageId: null,
        qtyMessage: typeof context.backorderQtyMessage === 'string' && context.backorderQtyMessage
            ? context.backorderQtyMessage
            : DEFAULT_QTY_MESSAGE,
        maxMessage: typeof context.quantityMaxMessage === 'string' && context.quantityMaxMessage
            ? context.quantityMaxMessage
            : DEFAULT_MAX_MESSAGE,
        sku: null,
        shipDate: null,
        shipByMessage: typeof context.backorderShipByMessage === 'string' && context.backorderShipByMessage
            ? context.backorderShipByMessage
            : DEFAULT_SHIP_BY_MESSAGE,
        dateOffsetDays: toInt(context.themeSettings && context.themeSettings.availability_date_offset_days, 0),
    };
}

export function mergeBackorderData(ctx, data = {}) {
    if (typeof data.sku === 'string') {
        // A new selection invalidates the previous variant's ship date. The first sku seen
        // keeps a date that may already have arrived (simple-product init: both fetches race).
        if (ctx.sku !== null && data.sku !== ctx.sku) ctx.shipDate = null;
        ctx.sku = data.sku;
    }
    if (asNumber(data.available_on_hand) !== null) {
        ctx.onHand = data.available_on_hand;
    } else if (asNumber(data.stock) !== null) {
        ctx.onHand = data.stock;
    }
    if (asNumber(data.available_for_backorder) !== null) {
        ctx.availableForBackorder = data.available_for_backorder;
    }
    if (asNumber(data.available_to_sell) !== null) {
        ctx.availableToSell = data.available_to_sell;
    }
    if (typeof data.unlimited_backorder === 'boolean') {
        ctx.unlimited = data.unlimited_backorder;
    }
    if (typeof data.show_backorder_availability_prompt === 'boolean') {
        ctx.showPrompt = data.show_backorder_availability_prompt;
    }
    if (typeof data.backorder_availability_prompt === 'string') {
        ctx.promptText = data.backorder_availability_prompt;
    }
    // Unconditional: a variant with no message must not inherit the previous one.
    ctx.messageId = data.backorder_message_id ?? null;
    return ctx;
}

export function backorderedQty(ctx, qty) {
    const requested = toInt(qty, 0);
    const cap = ctx.unlimited ? Infinity : ctx.availableForBackorder;
    return Math.max(0, Math.min(requested - ctx.onHand, cap));
}

export function sellLimitExceeded(ctx, qty) {
    if (ctx.unlimited || ctx.availableToSell <= 0) return null;
    return toInt(qty, 0) > ctx.availableToSell ? ctx.availableToSell : null;
}

export function defaultBackorderMessage(messages) {
    const found = Array.isArray(messages) ? messages.find(m => m && m.is_default === true) : null;
    return found && typeof found.message === 'string' ? found.message : '';
}

export function resolveBackorderMessage(ctx, qtyBackordered) {
    if (!ctx.showMessage || !(qtyBackordered > 0)) return '';
    const byId = ctx.messages.find(m => m && m.id === ctx.messageId);
    if (byId) return typeof byId.message === 'string' ? byId.message : '';
    // Store default: prefer the live ship-by date when the inventory service has one.
    const fallback = defaultBackorderMessage(ctx.messages);
    if (!fallback) return '';
    return formatShipBy(ctx, ctx.shipDate) || fallback;
}

function utcDay(date) {
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * onlineservices GetInventory result -> ship date, or null when there is nothing worth
 * promising: missing/unparseable, the .NET sentinel 0001-01-01, a past date (stale restock
 * data) or an item flagged do-not-reorder. Days compare in UTC: the service encodes a
 * US-Central midnight (05:00Z/06:00Z), which lands on the intended calendar day in UTC.
 */
export function parseNextAvailableDate(info, now = new Date()) {
    if (!info || info.isDoNotReorder === true || typeof info.nextAvailableDate !== 'string') return null;
    const date = new Date(info.nextAvailableDate);
    if (Number.isNaN(date.getTime())) return null;
    return utcDay(date) >= utcDay(now) ? date : null;
}

export function formatShipDate(date, offsetDays = 0) {
    const shifted = new Date(date.getTime() + toInt(offsetDays, 0) * DAY_MS);
    return shifted.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function formatShipBy(ctx, date) {
    if (!date) return '';
    return ctx.shipByMessage.replace('__DATE__', formatShipDate(date, ctx.dateOffsetDays));
}

/**
 * The service echoes the sku it actually looked up, which is the PREFIX-STRIPPED one on stores
 * whose appId carries a prefix rule (SimpleRetail asks for "B65848" and gets "65848" back), so
 * a selected sku ending with the returned one is the same product. Stripping only ever removes
 * a prefix, so the tolerance is one-directional and a genuinely stale variant still mismatches.
 */
function isSameSku(responseSku, selectedSku) {
    if (responseSku === selectedSku) return true;
    return Boolean(responseSku) && selectedSku.endsWith(responseSku);
}

export function mergeLiveInventory(ctx, info, now = new Date()) {
    // A late response for a previously selected variant must not overwrite the current one.
    if (ctx.sku !== null && info && info.sku != null && !isSameSku(String(info.sku), ctx.sku)) return ctx;
    ctx.shipDate = parseNextAvailableDate(info, now);
    return ctx;
}

export function canSell(ctx, data = {}) {
    const fromData = asNumber(data.available_to_sell);
    const availableToSell = fromData !== null ? fromData : ((ctx && ctx.availableToSell) || 0);
    return Boolean(data.instock) || availableToSell > 0;
}

export function showAvailabilityPrompt(ctx) {
    if (!ctx.showPrompt || !ctx.promptText) return false;
    if (ctx.unlimited) return true;
    return ctx.availableToSell > 0 && ctx.availableForBackorder > 0;
}
